import type {
  ProcedureFindingValue,
  ProcedureWorksheetData,
} from "$global-providers/rxdb-provider/collections/epocus-exam-worksheets";
import { Box, Chip, styled, TextField, Typography } from "@material-ui/core";
import { ChipRow, chipStyle } from "../../epocus-chip";
import {
  EPOCUS_BG,
  EPOCUS_BORDER,
  EPOCUS_TEXT_SECONDARY,
  rem,
} from "../../epocus-palette";
import type { HydratedExamImage } from "../../hooks/use-epocus-exam-images";
import { otherTextKey, type WorksheetOption } from "../worksheet-field-defs";
import {
  AllNormalToggle,
  EnumChipSelect,
  NotesField,
  NumericInput,
  ViewChecklist,
  WorksheetColumnLeft,
  WorksheetColumnRight,
  WorksheetColumns,
  WorksheetSection,
} from "../worksheet-primitives";
import {
  isProcedureFieldShown,
  isRoutineProcedure,
  normalizeProcedure,
  PROCEDURE_DEFINITIONS,
  toggleRoutineProcedure,
} from "./procedure-definitions";
import type { ProcedureFieldDef } from "./procedure-fields";

const ThumbGrid = styled(Box)({
  display: "flex",
  flexWrap: "wrap",
  gap: 12,
});

const ThumbCard = styled(Box)({
  width: 120,
  display: "flex",
  flexDirection: "column",
  gap: 6,
});

const ThumbFrame = styled(Box)({
  width: 120,
  height: 90,
  borderRadius: 8,
  overflow: "hidden",
  backgroundColor: EPOCUS_BG,
  border: `1px solid ${EPOCUS_BORDER}`,
});

const ThumbImg = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  display: "block",
});

const FieldLabel: FC<{ children: string }> = ({ children }) => (
  <Typography
    style={{
      fontSize: rem(13),
      fontWeight: 600,
      color: EPOCUS_TEXT_SECONDARY,
      marginTop: 4,
    }}
  >
    {children}
  </Typography>
);

const Hint: FC<{ children: string }> = ({ children }) => (
  <Typography style={{ fontSize: rem(13), color: EPOCUS_TEXT_SECONDARY }}>
    {children}
  </Typography>
);

const MultiChipSelect: FC<{
  options: readonly WorksheetOption[];
  selected: readonly string[];
  onChange: (selected: string[]) => void;
  disabled: boolean;
  label: string;
}> = ({ options, selected, onChange, disabled, label }) => (
  <ChipRow role="group" aria-label={label}>
    {options.map((option) => {
      const isSelected = selected.includes(option.value);
      return (
        <Chip
          key={option.value}
          label={option.label}
          variant="outlined"
          aria-pressed={isSelected}
          style={chipStyle(isSelected)}
          onClick={() =>
            onChange(
              isSelected
                ? selected.filter((value) => value !== option.value)
                : [...selected, option.value],
            )
          }
          disabled={disabled}
        />
      );
    })}
  </ChipRow>
);

const FreeText: FC<{
  value: ProcedureFindingValue | undefined;
  onChange: (value: string | null) => void;
  label: string;
  disabled: boolean;
}> = ({ value, onChange, label, disabled }) => (
  <TextField
    fullWidth
    size="small"
    variant="outlined"
    value={typeof value === "string" ? value : ""}
    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
      onChange(e.target.value || null)
    }
    disabled={disabled}
    inputProps={{ "aria-label": label }}
  />
);

const boundNumber = (
  field: Extract<ProcedureFieldDef, { kind: "number" }>,
  value: number | null,
): number | null => {
  if (value === null || Number.isNaN(value)) return null;
  const rounded = field.integer ? Math.round(value) : value;
  return field.max === undefined ? rounded : Math.min(rounded, field.max);
};

const ProcedureField: FC<{
  field: ProcedureFieldDef;
  findings: ProcedureWorksheetData["findings"];
  onChange: (id: string, value: ProcedureFindingValue) => void;
  disabled: boolean;
}> = ({ field, findings, onChange, disabled }) => {
  const value = findings[field.id];
  const otherKey = otherTextKey(field.id);
  const otherBox = (selected: readonly string[]) =>
    field.kind !== "number" &&
    field.kind !== "text" &&
    field.options.some(
      (option) => option.revealsText && selected.includes(option.value),
    ) && (
      <FreeText
        value={findings[otherKey]}
        onChange={(text) => onChange(otherKey, text)}
        label={`${field.label}, other`}
        disabled={disabled}
      />
    );

  switch (field.kind) {
    case "single": {
      const selected = typeof value === "string" ? value : null;
      return (
        <>
          <FieldLabel>{field.label}</FieldLabel>
          <EnumChipSelect
            options={[...field.options]}
            selected={selected}
            onChange={(next) => onChange(field.id, next)}
            disabled={disabled}
          />
          {otherBox(selected ? [selected] : [])}
        </>
      );
    }
    case "multi": {
      const selected = Array.isArray(value) ? value : [];
      return (
        <>
          <FieldLabel>{field.label}</FieldLabel>
          <MultiChipSelect
            options={field.options}
            selected={selected}
            onChange={(next) => onChange(field.id, next)}
            disabled={disabled}
            label={field.label}
          />
          {otherBox(selected)}
        </>
      );
    }
    case "number":
      return (
        <>
          <FieldLabel>{field.label}</FieldLabel>
          <NumericInput
            value={typeof value === "number" ? value : null}
            onChange={(next) => onChange(field.id, boundNumber(field, next))}
            unit={field.unit}
            step={field.integer ? 1 : 0.1}
            ariaLabel={field.label}
            disabled={disabled}
          />
        </>
      );
    case "text":
      return (
        <>
          <FieldLabel>{field.label}</FieldLabel>
          <FreeText
            value={value}
            onChange={(next) => onChange(field.id, next)}
            label={field.label}
            disabled={disabled}
          />
        </>
      );
  }
};

const NeedleInTargetImages: FC<{
  images: readonly HydratedExamImage[];
  onToggle?: (imageId: string, needleInTarget: boolean) => void;
  readOnly: boolean;
}> = ({ images, onToggle, readOnly }) => (
  <WorksheetSection label="Needle-in-target images">
    <Hint>Mark each image that shows the needle in the target.</Hint>
    <ThumbGrid>
      {images.map((image) => {
        const marked = image.needleInTarget === true;
        return (
          <ThumbCard key={image.id} data-testid={`procedure-image-${image.id}`}>
            <ThumbFrame>
              {image.previewUrl && (
                <ThumbImg
                  src={image.previewUrl}
                  alt={image.viewLabel ?? image.id}
                />
              )}
            </ThumbFrame>
            <Chip
              size="small"
              label="In target"
              variant="outlined"
              aria-pressed={marked}
              style={chipStyle(marked)}
              onClick={() => onToggle?.(image.id, !marked)}
              disabled={readOnly || !onToggle}
            />
          </ThumbCard>
        );
      })}
    </ThumbGrid>
  </WorksheetSection>
);

export const ProcedureWorksheet: FC<{
  data: ProcedureWorksheetData;
  onChange: (data: ProcedureWorksheetData) => void;
  readOnly: boolean;
  imageGallery?: React.ReactNode;
  images?: readonly HydratedExamImage[];
  onToggleNeedleInTarget?: (imageId: string, needleInTarget: boolean) => void;
}> = ({
  data,
  onChange,
  readOnly,
  imageGallery,
  images = [],
  onToggleNeedleInTarget,
}) => {
  const definition = PROCEDURE_DEFINITIONS[data.examType];
  const update = (next: ProcedureWorksheetData) =>
    onChange(normalizeProcedure(next));
  const setFinding = (id: string, value: ProcedureFindingValue) =>
    update({ ...data, findings: { ...data.findings, [id]: value } });
  const toggleNeedleInTarget = (imageId: string, needleInTarget: boolean) => {
    onToggleNeedleInTarget?.(imageId, needleInTarget);
    if (
      needleInTarget &&
      data.findings.needle_in_target_image_saved !== "yes"
    ) {
      setFinding("needle_in_target_image_saved", "yes");
    }
  };

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
      {definition.allNormal && (
        <AllNormalToggle
          isAllNormal={isRoutineProcedure(data)}
          onToggle={() => update(toggleRoutineProcedure(data))}
          disabled={readOnly}
          label="Routine procedure"
          description="Time-out, aseptic technique and advice done, no complications. Edit exceptions below."
        />
      )}

      <WorksheetColumns>
        <WorksheetColumnLeft>
          <WorksheetSection label="Indication">
            <MultiChipSelect
              options={definition.indications}
              selected={data.indication}
              onChange={(indication) => update({ ...data, indication })}
              disabled={readOnly}
              label="Indication"
            />
          </WorksheetSection>

          <WorksheetSection label="Views (optional)">
            <Hint>Tap to toggle obtained status</Hint>
            <ViewChecklist
              views={definition.views.map((view) => ({
                key: view.value,
                label: view.label,
              }))}
              checked={data.views}
              onChange={(key, checked) =>
                update({ ...data, views: { ...data.views, [key]: checked } })
              }
              disabled={readOnly}
            />
          </WorksheetSection>

          {imageGallery && (
            <WorksheetSection label="Images / Clips">
              {imageGallery}
            </WorksheetSection>
          )}

          {images.length > 0 && (
            <NeedleInTargetImages
              images={images}
              onToggle={onToggleNeedleInTarget && toggleNeedleInTarget}
              readOnly={readOnly}
            />
          )}
        </WorksheetColumnLeft>

        <WorksheetColumnRight>
          <WorksheetSection label="Findings">
            {definition.fields
              .filter((field) => isProcedureFieldShown(data.findings, field))
              .map((field) => (
                <Box key={field.id} display="flex" flexDirection="column">
                  <ProcedureField
                    field={field}
                    findings={data.findings}
                    onChange={setFinding}
                    disabled={readOnly}
                  />
                </Box>
              ))}
          </WorksheetSection>

          <WorksheetSection label="Notes">
            <NotesField
              value={data.notes}
              onChange={(notes) => update({ ...data, notes })}
              disabled={readOnly}
            />
          </WorksheetSection>
        </WorksheetColumnRight>
      </WorksheetColumns>
    </Box>
  );
};
